/**
 * Notities (notes) entity types.
 *
 * A notitie is a free-form note attached to exactly one business entity via a nullable
 * FK column. Notes are authored by the inspectiebedrijf (frontend) or an opdrachtgever
 * (portal). `zichtbaar_voor_portal` controls whether the opdrachtgever may see an
 * ibedrijf-written note. The `ibedrijf_id`, `auteur_type` and `created_by` columns are
 * stamped server-side by the `notities_set_author` trigger and must not be set by clients.
 *
 * `NotitieTable` replaces the generated `notities` table in the enhanced `Database` type
 * (see `types/database.enhanced.ts`) so the trigger-stamped columns stay optional on Insert.
 * Keep `notities` in that file's `Omit<GenTables, ...>` exclusion list — otherwise the
 * generated table intersects this one and forces those columns to be required again.
 */

/** Entities a note can be attached to (everything except ibedrijf). */
export type NotitieEntityType =
  | 'locatie'
  | 'opdrachtgever'
  | 'installateur'
  | 'inspecteur'
  | 'rapport'
  | 'offerte'
  | 'factuur'
  | 'offerte_bundel'

/** Foreign key column on `notities` for each entity type. */
export const NOTITIE_ENTITY_FK = {
  locatie: 'locatie_id',
  opdrachtgever: 'opdrachtgever_id',
  installateur: 'installateur_id',
  inspecteur: 'inspecteur_id',
  rapport: 'rapport_id',
  offerte: 'offerte_id',
  factuur: 'factuur_id',
  offerte_bundel: 'offerte_bundel_id',
} as const satisfies Record<NotitieEntityType, string>

export type NotitieEntityFk = (typeof NOTITIE_ENTITY_FK)[NotitieEntityType]

export type NotitieAuteurType = 'ibedrijf' | 'opdrachtgever' | 'installateur'

export type NotitieTable = {
  Row: {
    id: string
    locatie_id: string | null
    opdrachtgever_id: string | null
    installateur_id: string | null
    inspecteur_id: string | null
    rapport_id: string | null
    offerte_id: string | null
    factuur_id: string | null
    offerte_bundel_id: string | null
    inhoud: string
    zichtbaar_voor_portal: boolean
    reminder_at: string | null
    ibedrijf_id: string
    auteur_type: NotitieAuteurType
    created_by: string
    created_at: string
    updated_at: string | null
  }
  Insert: {
    id?: string
    locatie_id?: string | null
    opdrachtgever_id?: string | null
    installateur_id?: string | null
    inspecteur_id?: string | null
    rapport_id?: string | null
    offerte_id?: string | null
    factuur_id?: string | null
    offerte_bundel_id?: string | null
    inhoud: string
    zichtbaar_voor_portal?: boolean
    reminder_at?: string | null
    // Server-stamped by trigger — optional for clients.
    ibedrijf_id?: string
    auteur_type?: NotitieAuteurType
    created_by?: string
    created_at?: string
    updated_at?: string | null
  }
  Update: {
    id?: string
    inhoud?: string
    zichtbaar_voor_portal?: boolean
    reminder_at?: string | null
    updated_at?: string | null
  }
  Relationships: []
}

export type Notitie = NotitieTable['Row']
export type NotitieInsert = NotitieTable['Insert']
export type NotitieUpdate = NotitieTable['Update']
